import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
// @ts-ignore
import { dataIdFromObject } from 'lib/apollo/cache/memory';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import RemindAPI from 'lib/RemindAPI';
import CoachingConstants from 'coaching/CoachingConstants';
import errorLogger from 'utils/errorLogger';
import ACCEPT_COACHING_INVITATION from 'coaching/mutations/AcceptCoachingInvitation.gql';
import ADD_STUDENT_MUTATION from 'coaching/mutations/AddStudentToCoachingByDevice.gql';
import COORDINATOR_DELETE_COACHING_SESSION_MUTATION from 'coaching/mutations/CoordinatorDeleteCoachingSession.gql';
import CREATE_COACH_MUTATION from 'coaching/mutations/CreateCoach.gql';
import UPDATE_COACH_MUTATION from 'coaching/mutations/UpdateCoach.gql';
import CREATE_COACHING_RESERVATION_MUTATION from 'coaching/mutations/CreateCoachingReservation.gql';
import CREATE_COACHING_SESSION_MUTATION from 'coaching/mutations/CreateCoachingSession.gql';
import DELETE_COACHING_SESSION_MUTATION from 'coaching/mutations/DeleteCoachingSession.gql';
import DELETE_STUDENT_INVITATION_MUTATION from 'coaching/mutations/DeleteStudentInvitation.gql';
import GET_COACH_AVAILABILITY_FOR_EVENT_QUERY from 'coaching/queries/GetCoachAvailabilityForEvent.gql';
import GET_CREATE_COACH_FIELDS from 'coaching/queries/GetCreateCoachFields.gql';
import GET_COACH_STATUS_VALUES from 'coaching/queries/GetCoachStatusValues.gql';
import COACHES_QUERY from 'coaching/queries/Coaches.gql';
import GET_NAME_FOR_USER_UUID_QUERY from 'coaching/queries/GetNameForUserUuid.gql';
import GET_COACHING_USER_STRIPE_SOURCE from 'coaching/queries/GetUserStripeSource.gql';
import PATCH_PARTICIPANT_MUTATION from 'coaching/mutations/PatchParticipant.gql';
import PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithConnectedStudent.gql';
import PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithNoConnectedStudent.gql';
import RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION from 'coaching/mutations/RescheduleCoachingSessionInstance.gql';
import SAVE_AVAILABILITY_SCHEDULE_MUTATION from 'coaching/mutations/SaveAvailabilitySchedule.gql';
import SAVE_SINGLE_DAY_AVAILABILITY_OVERRIDE_MUTATION from 'coaching/mutations/SaveSingleDayAvailabilityOverride.gql';
import SET_COACHING_USER_STRIPE_SOURCE from 'coaching/mutations/SetCoachingUserStripeSource.gql';

import {
  CoachingEventDefinitionInput,
  CoachingParticipantInput,
  CoachingSessionInput,
  CoachAvailabilityInput,
  CoachAvailabilityExceptionInput,
  AvailabilityScheduleInput,
  SingleDayAvailabilityOverrideInput,
} from 'globalTypes';
import {
  AddStudentToCoachingByDevice,
  AddStudentToCoachingByDeviceVariables,
} from 'coaching/mutations/__generated__/AddStudentToCoachingByDevice';
import {
  CoordinatorDeleteCoachingSession,
  CoordinatorDeleteCoachingSessionVariables,
} from 'coaching/mutations/__generated__/CoordinatorDeleteCoachingSession';
import { CreateCoach, CreateCoachVariables } from 'coaching/mutations/__generated__/CreateCoach';
import {
  CreateCoachingReservation,
  CreateCoachingReservationVariables,
} from 'coaching/mutations/__generated__/CreateCoachingReservation';
import {
  CreateCoachingSession,
  CreateCoachingSessionVariables,
} from 'coaching/mutations/__generated__/CreateCoachingSession';
import {
  DeleteCoachingSession,
  DeleteCoachingSessionVariables,
} from 'coaching/mutations/__generated__/DeleteCoachingSession';
import {
  DeleteStudentInvitation,
  DeleteStudentInvitationVariables,
} from 'coaching/mutations/__generated__/DeleteStudentInvitation';
import {
  GetCoachAvailabilityForEvent,
  GetCoachAvailabilityForEventVariables,
} from 'coaching/queries/__generated__/GetCoachAvailabilityForEvent';
import {
  GetNameForUserUuid,
  GetNameForUserUuidVariables,
} from 'coaching/queries/__generated__/GetNameForUserUuid';
import { GetCreateCoachFields } from 'coaching/queries/__generated__/GetCreateCoachFields';
import { GetCoachStatusValues } from 'coaching/queries/__generated__/GetCoachStatusValues';
import {
  PatchParticipant,
  PatchParticipantVariables,
} from 'coaching/mutations/__generated__/PatchParticipant';
import {
  PurchaseCoachingProductWithConnectedStudent,
  PurchaseCoachingProductWithConnectedStudentVariables,
  PurchaseCoachingProductWithConnectedStudent_coaching_purchaseCoachingSessionWithConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithConnectedStudent';
import {
  PurchaseCoachingProductWithNoConnectedStudent,
  PurchaseCoachingProductWithNoConnectedStudentVariables,
  PurchaseCoachingProductWithNoConnectedStudent_coaching_purchaseCoachingSessionWithNoConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithNoConnectedStudent';
import {
  RescheduleCoachingSessionInstance,
  RescheduleCoachingSessionInstanceVariables,
} from 'coaching/mutations/__generated__/RescheduleCoachingSessionInstance';
import {
  SetUserStripeSource,
  SetUserStripeSourceVariables,
} from 'coaching/mutations/__generated__/SetUserStripeSource';
import {
  updateCoach as UpdateCoach,
  updateCoachVariables as UpdateCoachVariables,
} from 'coaching/mutations/__generated__/updateCoach';
import { errorFragment } from 'queries/__generated__/errorFragment';
import { AcceptCoachingInvitation } from 'coaching/mutations/__generated__/AcceptCoachingInvitation';
import ToastActions from 'actions/ToastActions';
import { Student } from 'coaching/containers/GetProspectiveStudentOptionsContainer';
import SET_COACHING_AVAILABILITY_EXCEPTION_MUTATION from 'coaching/mutations/SetCoachingAvailabilityException.gql';
import GET_COACH_AVAILABILITIES_AND_TIMEZONE_QUERY from 'coaching/queries/GetCoachAvailabilityAndCoachTimezone.gql';
import {
  SetCoachingAvailability,
  SetCoachingAvailabilityVariables,
} from 'coaching/mutations/__generated__/SetCoachingAvailability';
import DELETE_COACHING_AVAILABILITY_EXCEPTION_MUTATION from 'coaching/mutations/DeleteCoachingAvailabilityException.gql';
import GET_COACH_AVAILABILITIES_QUERY from 'coaching/queries/GetCoachAvailabilities.gql';
import SET_COACHING_AVAILABILITY_MUTATION from 'coaching/mutations/SetCoachingAvailability.gql';
import {
  SetCoachingAvailabilityException,
  SetCoachingAvailabilityExceptionVariables,
} from 'coaching/mutations/__generated__/SetCoachingAvailabilityException';
import {
  DeleteCoachingAvailabilityException,
  DeleteCoachingAvailabilityExceptionVariables,
} from 'coaching/mutations/__generated__/DeleteCoachingAvailabilityException';
import { AvailabilityQueryVariables } from 'coaching/containers/GetCoachAvailabilitiesAndTimezoneContainer';
import {
  SaveAvailabilitySchedule,
  SaveAvailabilityScheduleVariables,
} from 'coaching/mutations/__generated__/SaveAvailabilitySchedule';
import {
  SaveSingleDayAvailabilityOverride,
  SaveSingleDayAvailabilityOverrideVariables,
} from 'coaching/mutations/__generated__/SaveSingleDayAvailabilityOverride';

type PurchaseResult = {
  participantUuid: string | null;
  error?: { message: string; code?: string };
};

export function handleGQLError(error: errorFragment, consoleErrorText: string) {
  console.warn(error);
  console.error(`Coaching - ${consoleErrorText}`);
  ToastActions.showGQLError(error);
}

export function getCoachAvailabilityForEvent(
  coachUuid: string,
  eventDefinition: CoachingEventDefinitionInput,
  participantUuid: string
) {
  return RemindAPI.execute<GetCoachAvailabilityForEvent, GetCoachAvailabilityForEventVariables>({
    query: GET_COACH_AVAILABILITY_FOR_EVENT_QUERY,
    variables: {
      coachUuid,
      eventDefinition,
      participantUuid,
    },
    forceFetch: true,
  }).catch(err => {
    console.warn(err);
    console.error(
      `CoachingActions - could not get coach availability for event coach_uuid=${coachUuid} start_time=${eventDefinition.startDateTime} end_time=${eventDefinition.endDateTime} recurrence=${eventDefinition.recurrence}`
    );
  });
}

export function setUserStripeSource(stripeSourceId: string) {
  const handleError = (e: string, toastMessage: string | null) => {
    console.error(
      `CoachingActions - Could not add user card for stripeSourceId=${stripeSourceId}`,
      e
    );
    ToastActions.showErrorToast(
      toastMessage ?? 'There was a problem saving your card. Please try again.'
    );
  };

  return RemindAPI.execute<SetUserStripeSource, SetUserStripeSourceVariables>({
    mutation: SET_COACHING_USER_STRIPE_SOURCE,
    variables: {
      stripeSourceId,
      stripeAccountName: 'coaching',
    },
    refetchQueries: [
      {
        query: GET_COACHING_USER_STRIPE_SOURCE,
        variables: {
          stripeAccountName: 'coaching',
        },
      },
    ],
  })
    .then(res => {
      const { error, userFriendlyError } = res.setUserStripeSource;
      if (error) {
        handleError(error.message, userFriendlyError);
        return;
      }
      ToastActions.showSuccessToast('Payment method updated');
    })
    .catch(err => {
      handleError(err, null);
    });
}

export function createCoachingSession(
  input: CoachingSessionInput,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not create coaching session. err=${e.toString()}`);
    ToastActions.showErrorToast(e.toString());
    return;
  };
  return RemindAPI.execute<CreateCoachingSession, CreateCoachingSessionVariables>({
    mutation: CREATE_COACHING_SESSION_MUTATION,
    variables: { input },
    refetchQueries,
  })
    .then(res => {
      const { createCoachingSession } = res;
      if (!createCoachingSession || createCoachingSession.error) {
        return handleError(createCoachingSession?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(handleError);
}

export function rescheduleCoachingSessionInstance(
  coachingSessionInstanceUuid: string,
  newStartDateTime: string,
  newEndDateTime: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(
      `CoachingActions - could not reschedule coaching session instance. err=${e.toString()}`,
      { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime }
    );
    ToastActions.showErrorToast(e.toString());
    return;
  };
  return RemindAPI.execute<
    RescheduleCoachingSessionInstance,
    RescheduleCoachingSessionInstanceVariables
  >({
    mutation: RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION,
    variables: { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime },
    refetchQueries,
  })
    .then(res => {
      const { participantRescheduleCoachingSessionInstance } = res;
      if (
        !participantRescheduleCoachingSessionInstance ||
        participantRescheduleCoachingSessionInstance.error
      ) {
        return handleError(participantRescheduleCoachingSessionInstance?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
      ToastActions.showSuccessToast('Session successfully rescheduled');
    })
    .catch(handleError);
}

export function deleteCoachingSession(
  coachingSessionUuid: string,
  cancellationReason: string,
  coachingSessionInstanceUuid?: string,
  instanceStartDateTime?: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const errorText = `CoachingActions - could not delete session_uuid=${coachingSessionUuid} coaching_session_instance_uuid=${coachingSessionInstanceUuid}`;

  return RemindAPI.execute<DeleteCoachingSession, DeleteCoachingSessionVariables>({
    mutation: DELETE_COACHING_SESSION_MUTATION,
    variables: {
      coachingSessionUuid,
      instanceEventId: coachingSessionInstanceUuid,
      instanceStartDateTime,
      cancellationReason,
      coachingSessionInstanceUuid,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.deleteCoachingSession;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      onSuccess?.();
      ToastActions.showSuccessToast('Session successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete session');
    });
}

export function deleteStudentInvitation(participantUuid: string) {
  const errorText = `CoachingActions - could not delete student invitation for participant=${participantUuid}`;

  return RemindAPI.execute<DeleteStudentInvitation, DeleteStudentInvitationVariables>({
    mutation: DELETE_STUDENT_INVITATION_MUTATION,
    variables: {
      participantUuid,
    },
  })
    .then(res => {
      const { error } = res.coaching_deleteStudentInvitation;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast('Student invitation successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete student invitation');
    });
}

export function coordinatorDeleteCoachingSession(
  coachingSessionScheduleUuid: string,
  cancellationReason: string,
  waiveCustomerLateCharge: boolean,
  ongoingSeries: boolean,
  coachingSessionInstanceUuid?: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[]
) {
  const errorText = `CoachingActions - coordinator could not delete schedule_uuid=${coachingSessionScheduleUuid} instance_uuid=${coachingSessionInstanceUuid}`;
  const plural = ongoingSeries ? 's' : '';

  return RemindAPI.execute<
    CoordinatorDeleteCoachingSession,
    CoordinatorDeleteCoachingSessionVariables
  >({
    mutation: COORDINATOR_DELETE_COACHING_SESSION_MUTATION,
    variables: {
      coachingSessionScheduleUuid,
      cancellationReason,
      coachingSessionInstanceUuid: coachingSessionInstanceUuid ?? null,
      waiveCustomerLateCharge,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.coordinatorDeleteCoachingSession;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast(`Session${plural} successfully deleted`);
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(`Could not delete session${plural}`);
    });
}

export function createNewCoach(
  userUuid: string,
  subjects: string[],
  hourlyRateInCents: number,
  nameQuery: string
) {
  const errorText = `CoachingActions - could not create new coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<CreateCoach, CreateCoachVariables>({
    mutation: CREATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      hourlyRateInCents,
    },
    refetchQueries: [
      {
        query: COACHES_QUERY,
        variables: { nameQuery },
      },
    ],
  })
    .then(res => {
      const { error } = res.coaching_createCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function getNameForUserUuid(userUuid: string) {
  return RemindAPI.execute<GetNameForUserUuid, GetNameForUserUuidVariables>({
    query: GET_NAME_FOR_USER_UUID_QUERY,
    variables: {
      userUuid,
    },
  })
    .then(res => res.getNameForUserUuid)
    .catch(() => {
      ToastActions.showErrorToast('Could not find user’s name');
      return undefined;
    });
}

export function reserveCoachingSession(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  purchaserUuid: string,
  subject: string
): Promise<{ reservationUuid: string; expiresAt: string } | null> {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not reserve coaching session. err=${e}`);
    ToastActions.showErrorToast(
      'There was a problem reserving your coaching session. Please try again.'
    );
    return null;
  };
  return RemindAPI.execute<CreateCoachingReservation, CreateCoachingReservationVariables>({
    mutation: CREATE_COACHING_RESERVATION_MUTATION,
    variables: { subject, purchaserUuid, startDateTime, endDateTime, coachUuid },
  })
    .then(res => {
      const { createCoachingReservation: reservation } = res;
      if (!reservation?.reservationUuid || reservation.error) {
        return handleError(reservation?.error?.message!);
      }
      return {
        reservationUuid: reservation.reservationUuid,
        expiresAt: reservation.expiresAt!,
      };
    })
    .catch(handleError);
}

export function purchaseCoaching(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  stripeSource: string,
  purchaserUuid: string,
  timezone: string,
  subjects: string[],
  student: Partial<Student> & { name: string },
  studentGradeLevel: string,
  reservationUuid: string
): Promise<PurchaseResult> {
  const logError = (err: any, level: 'warn' | 'error'): void => {
    const text = 'Could not purchase coaching';
    const args = {
      purchaserUuid,
      studentUuid: student.uuid,
      coachUuid,
      startDateTime,
      reservationUuid,
      err,
    };

    if (level === 'error') {
      errorLogger(text)(args);
    } else if (level === 'warn') {
      console.warn(text, args);
    }
  };

  const handlePurchaseResponse = (
    res:
      | PurchaseCoachingProductWithConnectedStudent_coaching_purchaseCoachingSessionWithConnectedStudent
      | PurchaseCoachingProductWithNoConnectedStudent_coaching_purchaseCoachingSessionWithNoConnectedStudent
  ): Promise<PurchaseResult> => {
    const { participantUuid, error: resError } = res;

    if (resError) {
      // no need to log as an error since these are expected errors
      logError(resError, 'warn');
    }

    return Promise.resolve({
      participantUuid,
      error: resError ? { message: resError.message, code: resError.code ?? undefined } : undefined,
    });
  };

  const sharedVars = {
    startDateTime,
    endDateTime,
    coachUuid,
    stripeSource,
    purchaserUuid,
    timezone,
    subjects,
    studentGradeLevel,
    reservationUuid,
  };

  const promise = student.uuid
    ? RemindAPI.execute<
        PurchaseCoachingProductWithConnectedStudent,
        PurchaseCoachingProductWithConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentUuid: student.uuid },
      }).then(res =>
        handlePurchaseResponse(res.coaching_purchaseCoachingSessionWithConnectedStudent)
      )
    : RemindAPI.execute<
        PurchaseCoachingProductWithNoConnectedStudent,
        PurchaseCoachingProductWithNoConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentName: student.name },
      }).then(res =>
        handlePurchaseResponse(res.coaching_purchaseCoachingSessionWithNoConnectedStudent)
      );

  return promise.catch(err => {
    // if we get here, it means we encountered an unexpected error
    // so we should log as an error
    logError(err, 'error');
    return {
      participantUuid: null,
      error: { message: 'We were unable to complete your coaching purchase' },
    };
  });
}

export function patchParticipant(uuid: string, participant: CoachingParticipantInput) {
  const errorText = `CoachingActions - could not create modify participant uuid=${uuid}`;
  return RemindAPI.execute<PatchParticipant, PatchParticipantVariables>({
    mutation: PATCH_PARTICIPANT_MUTATION,
    variables: {
      uuid,
      participant,
    },
  })
    .then(res => {
      const { error } = res.patchParticipant;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateParticipant(uuid, participant);
      ToastActions.showSuccessToast('Participant successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify participant');
      return { success: false };
    });
}

export function addStudentByDevice(
  deviceAddress: string,
  participantUUID: string,
  userEmail?: string
) {
  const errorText = `Could not add student`;
  return RemindAPI.execute<AddStudentToCoachingByDevice, AddStudentToCoachingByDeviceVariables>({
    mutation: ADD_STUDENT_MUTATION,
    variables: {
      deviceAddress,
      participantUUID,
    },
  })
    .then(res => {
      const { error } = res.addStudentToCoachingByDevice;
      if (error) {
        console.warn(error);
        return { success: false };
      }
      ToastActions.showSuccessToast(
        `We’ve sent an invitation to ${deviceAddress}${userEmail &&
          ` and a confirmation to ${userEmail}`}`
      );
      return { success: true };
    })
    .catch(err => {
      errorLogger(errorText)({ err, participantUUID });
      ToastActions.showErrorToast(
        `We were unable to add your student’s account at this time. Please email us at ${CoachingConstants.COACHING_HELP_EMAIL} for further assistance.`
      );
      return { success: false };
    });
}

export function acceptCoachingInvitation() {
  const errorText = 'User could not accept coaching invitation';

  return RemindAPI.execute<AcceptCoachingInvitation>({ mutation: ACCEPT_COACHING_INVITATION })
    .then(res => {
      const { error } = res.acceptCoachingInvitation;

      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return { success: false };
      }
      ToastActions.showSuccessToast('Coaching invitation was accepted');
      return { success: true };
    })
    .catch(err => {
      errorLogger(errorText)({ err });
      ToastActions.showErrorToast('We were unable to confirm your invitation to coaching');
      return { success: false };
    });
}

function updateParticipant(uuid: string, participantInput: CoachingParticipantInput) {
  // updates a specific participant in the Apollo cache
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'CoachingParticipant', uuid }),
    fragment: gql`
      fragment participant on CoachingParticipant {
        studentName
        studentUuid
        whiteboardUrl
      }
    `,
    variables: { uuid },
    data: {
      studentUuid: participantInput.studentUuid,
      studentName: participantInput.studentName,
      whiteboardUrl: participantInput.whiteboardUrl,
      __typename: 'CoachingParticipant',
    },
  });
}

export function setCoachingAvailability(
  availabilitiesByDay: CoachAvailabilityInput[],
  timezone: string,
  coachUuid: string,
  availabilityQuery: AvailabilityQueryVariables
) {
  return RemindAPI.execute<SetCoachingAvailability, SetCoachingAvailabilityVariables>({
    mutation: SET_COACHING_AVAILABILITY_MUTATION,
    variables: {
      input: availabilitiesByDay,
      timezone,
      coachUuid,
    },
    refetchQueries: [
      {
        query: GET_COACH_AVAILABILITIES_AND_TIMEZONE_QUERY,
        variables: availabilityQuery,
      },
    ],
  })
    .then(res => {
      ToastActions.showSuccessToast('Saved your normal availability');
    })
    .catch(error => {
      ToastActions.showErrorToast(error.message);
    });
}

export function deleteCoachAvailabilityException(
  exceptionUuid: string,
  coachUuid: string,
  availabilityQuery: AvailabilityQueryVariables
) {
  return RemindAPI.execute<
    DeleteCoachingAvailabilityException,
    DeleteCoachingAvailabilityExceptionVariables
  >({
    mutation: DELETE_COACHING_AVAILABILITY_EXCEPTION_MUTATION,
    variables: {
      exceptionUuid,
      coachUuid,
    },
    refetchQueries: [
      {
        query: GET_COACH_AVAILABILITIES_QUERY,
        variables: availabilityQuery,
      },
    ],
  })
    .then(res => {
      ToastActions.showSuccessToast('Removed your time off block');
    })
    .catch(error => {
      ToastActions.showErrorToast(error.message);
    });
}

export function setCoachingAvailabilityExceptions(
  availabilityExceptions: CoachAvailabilityExceptionInput[],
  timezone: string,
  coachUuid: string,
  availabilityQuery: AvailabilityQueryVariables
) {
  return RemindAPI.execute<
    SetCoachingAvailabilityException,
    SetCoachingAvailabilityExceptionVariables
  >({
    mutation: SET_COACHING_AVAILABILITY_EXCEPTION_MUTATION,
    variables: {
      input: availabilityExceptions,
      timezone,
      coachUuid,
    },
    refetchQueries: [
      {
        query: GET_COACH_AVAILABILITIES_QUERY,
        variables: availabilityQuery,
      },
    ],
  })
    .then(res => {
      ToastActions.showSuccessToast('Saved your time off');
    })
    .catch(error => {
      ToastActions.showErrorToast(
        'Oops - we weren’t able to save your time off. You may already have a coaching session booked at this time - please contact us to resolve the conflict.'
      );
    });
}

export function getCoachCreateFields(): Promise<{
  subjects?: string[];
  hourlyRateInCents?: number[];
  error?: string;
}> {
  return RemindAPI.execute<GetCreateCoachFields>({
    query: GET_CREATE_COACH_FIELDS,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      const subjectsResult = res.coaching_coachableSubjects;
      const hourRatesResult = res.coaching_coachingHourlyRates;
      return {
        subjects: subjectsResult?.subjects || [],
        hourlyRateInCents: hourRatesResult?.hourlyRatesInCents || [],
      };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get coach fields create container');
      return { error: err.message };
    });
}

export function getCoachStatusValues() {
  return RemindAPI.execute<GetCoachStatusValues>({
    query: GET_COACH_STATUS_VALUES,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      const coachStatusesResult = res.coaching_coachStatuses;
      return { statuses: coachStatusesResult?.statuses };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get coach fields create container');
      return { error: err };
    });
}

export function updateCoach(
  userUuid: string,
  subjects: string[],
  status: string,
  nameQuery: string
) {
  const errorText = `CoachingActions - could not create update coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<UpdateCoach, UpdateCoachVariables>({
    mutation: UPDATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      status,
    },
    refetchQueries: [
      {
        query: COACHES_QUERY,
        variables: { nameQuery },
      },
    ],
  })
    .then(res => {
      const { error } = res.coaching_updateCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function saveAvailabilitySchedule(
  schedule: AvailabilityScheduleInput,
  timezone: string,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not save availability schedule for coach';
  return RemindAPI.execute<SaveAvailabilitySchedule, SaveAvailabilityScheduleVariables>({
    mutation: SAVE_AVAILABILITY_SCHEDULE_MUTATION,
    variables: {
      schedule,
      timezone,
    },
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_saveCoachAvailabilitySchedule;
      if (error) {
        ToastActions.showErrorToast(
          `There was a problem updating your schedule. If you continue to encounter problems, please contact support at ${CoachingConstants.COACHING_HELP_EMAIL}.`
        );
        return;
      }
      ToastActions.showSuccessToast('Schedule saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ schedule, timezone, err });
      ToastActions.showErrorToast('Could not save schedule');
    });
}

export function saveSingleDayAvailabilityOverride(
  override: SingleDayAvailabilityOverrideInput,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not save single day availability override for coach';
  return RemindAPI.execute<
    SaveSingleDayAvailabilityOverride,
    SaveSingleDayAvailabilityOverrideVariables
  >({
    mutation: SAVE_SINGLE_DAY_AVAILABILITY_OVERRIDE_MUTATION,
    variables: {
      override,
    },
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_saveSingleDayAvailabilityOverride;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Availability saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ override, err });
      ToastActions.showErrorToast('Could not save availability');
    });
}
